/**
 * ValidateAccessCodeQuery - Query and Handler for validating developer access codes
 * Follows MediatR pattern (Query + Handler in same file)
 */

import { logger } from '@/application/core';
import type { IQuery, IQueryHandler, IHttpClient } from '@/application/core';
import type { ValidateAccessCodeResponse } from './developerRegistration.types';
import { toCamelCase } from '@/utils/caseTransform';

// ============================================================================
// Query (Request)
// ============================================================================

/**
 * Query to validate developer access code
 */
export class ValidateAccessCodeQuery implements IQuery<ValidateAccessCodeResponse> {
  readonly type = 'ValidateAccessCodeQuery' as const;

  constructor(
    public readonly accessCode: string,
    public readonly antiforgeryToken: string
  ) {}
}

// ============================================================================
// Handler
// ============================================================================

/**
 * Handler for ValidateAccessCodeQuery
 * Uses dependency injection for testability
 */
export class ValidateAccessCodeQueryHandler implements IQueryHandler<
  ValidateAccessCodeQuery,
  ValidateAccessCodeResponse
> {
  private readonly validateUrl = '/developer-registration-validate-code';

  constructor(private readonly httpClient: IHttpClient) {}

  async handle(query: ValidateAccessCodeQuery): Promise<ValidateAccessCodeResponse> {
    try {
      // Convert to form-urlencoded (matching legacy jQuery pattern)
      const formData = new URLSearchParams();
      formData.append('CSRF-TOKEN-ECGRID-FORM', query.antiforgeryToken);
      formData.append('AccessCode', query.accessCode); // PascalCase to match backend DTO

      // Make POST request with form-urlencoded content type
      // Backend may return snake_case, transform to camelCase for consistency
      const response = await this.httpClient.post<any>(this.validateUrl, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // Transform response - toCamelCase handles snake_case/PascalCase keys,
      // fallback with ?? handles responses already in camelCase
      const transformed = toCamelCase(response);
      return {
        isValid: transformed.isValid ?? response.isValid,
        accessCodeType: transformed.accessCodeType ?? response.accessCodeType,
        salespersonEmail: transformed.salespersonEmail ?? response.salespersonEmail,
        errorMessage: transformed.errorMessage ?? response.errorMessage,
      } as ValidateAccessCodeResponse;
    } catch (error) {
      // Transform error to expected response format
      logger.error('ValidateAccessCodeQueryHandler', 'Error:', error);

      return {
        isValid: false,
        accessCodeType: '',
        salespersonEmail: null,
        errorMessage: 'Failed to validate access code',
      };
    }
  }
}
